// components/modals/StockAdjustmentModal.tsx

import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { convertReadable, formatInputWithComma, convertDateWithHyphen } from '../../../../lib/converting-func';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from '../../../../redux/reducers';
import { getDate } from '../../../../lib/util-func';

interface StockAdjustmentModalProps {
  show: boolean;
  onHide: () => void;
  negativeItems: any[]; // 백엔드에서 받은 { item, item_nm, cur_qty, unit ... }
  autoStockAddValue: number | null;
  onConfirm: (adjustItems: { item: string; qty: number; wh: string; ymd: string }[]) => void;
}

export default function StockAdjustmentModal({ show, onHide, negativeItems, autoStockAddValue, onConfirm }: StockAdjustmentModalProps) {
  // 사용자가 입력할 보정 수량 상태 관리
  // 구조: { [itemCode]: string(입력값) }
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  // 사용자가 선택할 입고일자 상태 관리
  // 구조: { [itemCode]: string(YYYYMMDD) }
  const [adjustDates, setAdjustDates] = useState<{ [key: string]: string }>({});

  const { dec } = useSelector((state: RootReducer) => state.dec, shallowEqual)

  // 모달 열릴 때 초기값 세팅 (보정 수량은 마이너스 수량의 절대값에 autoStockAddValue를 더한 값으로 세팅)
  useEffect(() => {
    if (show && negativeItems.length > 0) {
      const initial: { [key: string]: string } = {};
      const initialDates: { [key: string]: string } = {};
      const todayStr = getDate();
      negativeItems.forEach((row) => {
        if (autoStockAddValue === null) {
          initial[row.item] = '';
        } else {
          const val = Math.abs(row.cur_qty || 0) + autoStockAddValue;
          initial[row.item] = formatInputWithComma(val.toString());
        }
        initialDates[row.item] = todayStr;
      });
      setInputValues(initial);
      setAdjustDates(initialDates);
    }
  }, [show, negativeItems, autoStockAddValue]);

  // 입력 핸들러
  const handleChange = (item: string, value: string) => {
    const formatted = formatInputWithComma(value);
    setInputValues((prev) => ({ ...prev, [item]: formatted }));
  };

  // 날짜 변경 핸들러
  const handleDateChange = (item: string, value: string) => {
    const rawDate = value.replace(/-/g, ''); // YYYY-MM-DD -> YYYYMMDD
    setAdjustDates((prev) => ({ ...prev, [item]: rawDate }));
  };

  // 저장 핸들러
  const handleSave = async () => {
    // 0 이상 입력된 항목만 추려서 전송
    const adjustItems = negativeItems.map(row => {
      const qtyStr = (inputValues[row.item] || '0').replace(/,/g, '');
      return {
        item: row.item,
        qty: parseFloat(qtyStr),
        wh: '01', // 기본 창고
        ymd: adjustDates[row.item] || getDate()
      };
    }).filter(row => row.qty > 0);

    if (adjustItems.length === 0) {
      alert("조정할 수량을 입력해주세요.");
      return;
    }

    onConfirm(adjustItems);
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title className="text-danger fw-bold">
          마이너스 재고 발생
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3 text-secondary">
          아래 품목의 재고가 부족합니다.<br/>
          부족한 수량을 <b>재고 조정(입고)</b> 처리하시겠습니까?
        </p>

        <div className="table-responsive">
          <table className="table table-bordered table-sm text-center align-middle">
            <thead className="table-light">
              <tr>
                <th>품명</th>
                <th>현재고</th>
                <th>입고일자</th>
                <th>보정 수량</th>
              </tr>
            </thead>
            <tbody>
              {negativeItems.map((row, idx) => (
                <tr key={idx}>
                  <td className="text-start px-2">{row.item_nm}</td>
                  <td className="text-danger fw-bold">
                    {/* 소수점 등 포맷팅 적용 */}
                    {convertReadable(row.cur_qty.toString(), dec)}
                  </td>
                  <td style={{ width: '150px' }}>
                    <input
                      type="date"
                      className="form-control form-control-sm text-center"
                      value={convertDateWithHyphen(adjustDates[row.item] || getDate())}
                      onChange={(e) => handleDateChange(row.item, e.target.value)}
                    />
                  </td>
                  <td style={{ width: '100px' }}>
                    <input
                      type="text"
                      className="form-control form-control-sm text-end"
                      value={inputValues[row.item] || ''}
                      onChange={(e) => handleChange(row.item, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>닫기</button>
        <button className="btn btn-primary" onClick={handleSave}>저장</button>
      </Modal.Footer>
    </Modal>
  );
}